import { create } from 'zustand';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  writeBatch,
  query,
  where,
  onSnapshot,
  updateDoc,
} from 'firebase/firestore';
import { firestore } from '../lib/firebase/config';
import { Dispute, DisputingGuide } from '../types/dispute';
import { Evidence } from '../types/pilgrimGuide';
import { COLLECTIONS } from './pilgrimGuide/constants';

type DisputesState = {
  disputes: Record<string, Dispute>;
  loading: boolean;
  error: string | null;
  fetchDisputes: () => Promise<void>;
  submitEvidence: (
    disputeId: string,
    guideId: string,
    evidence: { text: string; files: Array<{ url: string; type: 'image' | 'pdf'; name: string; }> }
  ) => Promise<void>;
  resolveDispute: (disputeId: string, resolvedGuideId: string) => Promise<void>;
};

export const useDisputesStore = create<DisputesState>((set, get) => ({
  disputes: {},
  loading: false,
  error: null,

  fetchDisputes: async () => {
    set({ loading: true, error: null });
    try {
      console.log('Fetching disputes...');
      const querySnapshot = await getDocs(
        collection(firestore, COLLECTIONS.DISPUTES)
      );
      const disputes: Record<string, Dispute> = {};

      // Fetch all guides first
      const guidesSnapshot = await getDocs(collection(firestore, 'Guides'));
      const guidesMap = new Map();
      guidesSnapshot.docs.forEach(doc => {
        guidesMap.set(doc.id, doc.data());
      });

      // Fetch all organizers
      const organizersSnapshot = await getDocs(collection(firestore, 'Organizers'));
      const organizersMap = new Map();
      organizersSnapshot.docs.forEach(doc => {
        organizersMap.set(doc.id, doc.data());
      });

      // Fetch all pilgrims to get package information
      const pilgrimsSnapshot = await getDocs(collection(firestore, 'Pilgrims'));
      const pilgrimsMap = new Map();
      pilgrimsSnapshot.docs.forEach(doc => {
        pilgrimsMap.set(doc.id, doc.data());
      });

      console.log('Data maps created:', {
        guidesCount: guidesMap.size,
        organizersCount: organizersMap.size,
        pilgrimsCount: pilgrimsMap.size
      });

      for (const doc of querySnapshot.docs) {
        const disputeData = doc.data();
        console.log(`Processing dispute ${doc.id}:`, disputeData);

        // Get pilgrim package name
        const pilgrimData = pilgrimsMap.get(disputeData.pilgrimId);
        const packageName = pilgrimData?.package_nickName;

        // Process each guide in the dispute
        const processedGuides = await Promise.all(disputeData.disputingGuides.map(async (guide: any) => {
          console.log(`Processing guide in dispute ${doc.id}:`, guide);

          const guideData = guidesMap.get(guide.guideId);
          if (!guideData) {
            console.warn(`Guide ${guide.guideId} not found in guides map`);
            return guide;
          }

          const organizerData = organizersMap.get(guideData.organizerId);
          if (!organizerData) {
            console.warn(`Organizer ${guideData.organizerId} not found for guide ${guide.guideId}`);
          }

          console.log('Guide and organizer data:', {
            guideId: guide.guideId,
            guideName: guideData.name,
            organizerId: guideData.organizerId,
            organizerName: organizerData?.name
          });

          return {
            ...guide,
            guideName: guideData.name,
            organizerName: organizerData?.name || guide.organizerName || 'Not specified'
          };
        }));

        disputes[doc.id] = {
          ...disputeData,
          id: doc.id,
          disputingGuides: processedGuides,
          packageName: packageName // Add package name to dispute
        } as Dispute;
      }

      console.log('Final disputes data:', {
        count: Object.keys(disputes).length,
        disputes: Object.values(disputes).map(d => ({
          id: d.id,
          pilgrimName: d.pilgrimName,
          packageName: d.packageName,
          guides: d.disputingGuides.map(g => ({
            guideName: g.guideName,
            organizerName: g.organizerName
          }))
        }))
      });

      set({ disputes, loading: false });
    } catch (error: any) {
      console.error('Error fetching disputes:', error);
      set({ error: error.message || 'فشل في جلب النزاعات', loading: false });
    }
  },

  submitEvidence: async (disputeId: string, guideId: string, evidenceData: { text: string; files: Array<{ url: string; type: 'image' | 'pdf'; name: string; }> }) => {
    try {
      const disputeRef = doc(firestore, COLLECTIONS.DISPUTES, disputeId);
      const disputeDoc = await getDoc(disputeRef);

      if (!disputeDoc.exists()) {
        throw new Error('النزاع غير موجود');
      }

      const dispute = disputeDoc.data() as Dispute;
      const now = Date.now();

      const evidence: Evidence = {
        text: evidenceData.text,
        files: evidenceData.files,
        submittedAt: now,
        updatedAt: now,
      };

      const updatedGuides: DisputingGuide[] = dispute.disputingGuides.map(
        (guide) => {
          if (guide.guideId === guideId) {
            return {
              ...guide,
              evidence,
            };
          }
          return guide;
        }
      );

      const updates = {
        disputingGuides: updatedGuides,
        status: 'under_processing' as const,
        updatedAt: now,
      };

      await updateDoc(disputeRef, updates);

      set((state) => ({
        disputes: {
          ...state.disputes,
          [disputeId]: {
            ...state.disputes[disputeId],
            ...updates,
          },
        },
      }));
    } catch (error: any) {
      console.error('Error submitting evidence:', error);
      throw new Error(error.message || 'فشل في تقديم الإثبات');
    }
  },

  resolveDispute: async (disputeId: string, resolvedGuideId: string) => {
    try {
      const batch = writeBatch(firestore);

      // Get dispute details
      const disputeRef = doc(firestore, COLLECTIONS.DISPUTES, disputeId);
      const disputeDoc = await getDoc(disputeRef);

      if (!disputeDoc.exists()) {
        throw new Error('النزاع غير موجود');
      }

      const dispute = disputeDoc.data() as Dispute;
      const now = Date.now();

      // Create ban for losing guide
      const losingGuide = dispute.disputingGuides.find(
        (g) => g.guideId !== resolvedGuideId
      );
      if (losingGuide) {
        const banRef = doc(collection(firestore, COLLECTIONS.GUIDE_BANS));
        batch.set(banRef, {
          guideId: losingGuide.guideId,
          pilgrimId: dispute.pilgrimId,
          disputeId: disputeId,
          createdAt: now,
        });
      }

      // Update PilgrimGuideLinks
      const linksRef = collection(firestore, COLLECTIONS.LINKS);
      const linksQuery = query(
        linksRef,
        where('pilgrimId', '==', dispute.pilgrimId)
      );
      const linksSnapshot = await getDocs(linksQuery);

      linksSnapshot.forEach((doc) => {
        const linkData = doc.data();
        if (linkData.guideId === resolvedGuideId) {
          // Approve the winning guide's link
          batch.update(doc.ref, {
            status: 'approved',
            disputeId: null,
            updatedAt: now,
          });
        } else {
          // Delete other guides' links
          batch.delete(doc.ref);
        }
      });

      // Update ownership record
      const ownershipRef = collection(firestore, 'PilgrimOwnership');
      const ownershipQuery = query(
        ownershipRef,
        where('pilgrimId', '==', dispute.pilgrimId)
      );
      const ownershipSnapshot = await getDocs(ownershipQuery);

      if (!ownershipSnapshot.empty) {
        // Update existing ownership record
        const ownershipDoc = ownershipSnapshot.docs[0];
        batch.update(ownershipDoc.ref, {
          realGuideId: resolvedGuideId,
          ownerGuideId: resolvedGuideId,
          updatedAt: now,
        });
      } else {
        // Create new ownership record
        const newOwnershipRef = doc(collection(firestore, 'PilgrimOwnership'));
        batch.set(newOwnershipRef, {
          pilgrimId: dispute.pilgrimId,
          realGuideId: resolvedGuideId,
          ownerGuideId: resolvedGuideId,
          createdAt: now,
          updatedAt: now,
        });
      }

      // Delete the dispute document
      batch.delete(disputeRef);

      await batch.commit();

      // Update local state
      set((state) => {
        const newDisputes = { ...state.disputes };
        delete newDisputes[disputeId];
        return { disputes: newDisputes };
      });
    } catch (error: any) {
      console.error('Error resolving dispute:', error);
      throw new Error(error.message || 'فشل في حل النزاع');
    }
  },
}));